<template>
  <footer class="footer">
    <!-- Gradient Line -->
    <div class="footer__gradient-line"></div>

    <div class="footer__container">
      <!-- Logo and Description -->
      <div class="footer__brand">
        <router-link to="/" class="footer__logo">
          <img src="/logo.png" alt="Kernel Pilot" class="footer__logo-img" />
          <span class="footer__logo-text">Kernel Pilot</span>
        </router-link>
        <p class="footer__tagline">
          {{ t('footer.tagline') }}
        </p>
      </div>

      <!-- Quick Links -->
      <div class="footer__links">
        <h4 class="footer__links-title">{{ t('footer.quickLinks') }}</h4>
        <nav class="footer__nav">
          <router-link to="/" class="footer__link">{{ t('nav.home') }}</router-link>
          <router-link to="/playground" class="footer__link">{{ t('nav.playground') }}</router-link>
          <router-link to="/blog" class="footer__link">{{ t('nav.blog') }}</router-link>
          <a href="https://github.com/KernelPilot" target="_blank" rel="noopener" class="footer__link">
            GitHub
          </a>
        </nav>
      </div>

      <!-- Contact -->
      <div class="footer__contact">
        <h4 class="footer__links-title">{{ t('footer.connect') }}</h4>
        <div class="footer__social">
          <a 
            href="https://github.com/KernelPilot" 
            target="_blank" 
            rel="noopener"
            class="footer__social-link"
            aria-label="GitHub"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
          </a>
          <a 
            href="mailto:KernelPilot_Suggestion@outlook.com" 
            class="footer__social-link"
            aria-label="Email"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </a>
        </div>
      </div>
    </div>

    <!-- Copyright -->
    <div class="footer__bottom">
      <p class="footer__copyright">{{ t('footer.copyright') }}</p>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
</script>

<style scoped>
.footer {
  background: var(--color-bg-secondary);
  margin-top: auto;
  position: relative;
}

.footer__gradient-line {
  height: 2px;
  background: var(--gradient-primary);
}

.footer__container {
  max-width: var(--container-xl);
  margin: 0 auto;
  padding: var(--spacing-16) var(--spacing-6);
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: var(--spacing-12);
}

/* Brand */
.footer__brand {
  max-width: 400px;
}

.footer__logo {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-3);
  text-decoration: none;
  margin-bottom: var(--spacing-4);
}

.footer__logo-img {
  width: 36px;
  height: auto;
  transition: transform var(--transition-base);
}

.footer__logo-text {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.footer__tagline {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  line-height: var(--line-height-relaxed);
  margin: 0;
}

/* Links */
.footer__links-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: var(--spacing-4);
}

.footer__nav {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.footer__link {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.footer__link:hover {
  color: var(--color-accent-cyan);
}

/* Social Links */
.footer__social {
  display: flex;
  gap: var(--spacing-3);
}

.footer__social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  transition: all var(--transition-fast);
}

.footer__social-link svg {
  width: 20px;
  height: 20px;
}

.footer__social-link:hover {
  color: var(--color-text-primary);
  border-color: var(--color-primary);
  box-shadow: var(--shadow-glow-primary);
  transform: translateY(-2px);
}

/* Bottom */
.footer__bottom {
  border-top: 1px solid var(--color-border);
  padding: var(--spacing-6);
  text-align: center;
}

.footer__copyright {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .footer__container {
    grid-template-columns: 1fr;
    gap: var(--spacing-8);
    padding: var(--spacing-10) var(--spacing-6);
  }

  .footer__brand {
    max-width: none;
    text-align: center;
  }

  .footer__logo {
    justify-content: center;
  }

  .footer__links,
  .footer__contact {
    text-align: center;
  }

  .footer__nav,
  .footer__social {
    justify-content: center;
  }
}
</style>
